import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useMemo, memo } from 'react';
import { Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import dayjs from 'dayjs';
import { ImageHeader } from '../DocumentThirteen/components';
import { BorderPolygon, CardType, Checkbox } from './components';
;
export const DocumentFifteen = memo(({ pharmacy, traderInfo, }) => {
    const subtitle = {
        fontFamily: 'Roboto',
        fontStyle: 'bold',
        fontWeight: 700,
        fontSize: 10,
        marginBottom: 10,
        textTransform: 'uppercase',
    };
    const textSm = { fontSize: 9, };
    const styles = StyleSheet.create({
        container: {
            height: '100%',
        },
        header: {
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexWrap: 'nowrap',
            margin: '16px 0px',
        },
        slogan: {
            color: '#2d65a2',
            fontSize: 9,
            paddingRight: 16,
        },
        content: {
            padding: 40,
        },
        title: {
            fontFamily: 'Roboto',
            fontStyle: 'bold',
            fontWeight: 700,
            fontSize: 15,
            textAlign: 'center',
        },
        field: {
            marginBottom: 8,
            fontSize: 11,
            width: '100%',
        },
        subtitle,
        subtitle2: {
            ...subtitle,
            marginBottom: 5,
            textTransform: 'none',
            fontStyle: 'normal',
            fontWeight: 'normal',
        },
        subtitleIndented: {
            ...subtitle,
            marginBottom: 8,
            textTransform: 'none',
            paddingLeft: 38,
        },
        section: {
            marginTop: 15,
        },
        section2: {
            marginTop: 10,
        },
        textSm,
        textSm2: {
            ...textSm,
            paddingTop: 8,
        },
        textSm2Bold: {
            ...textSm,
            paddingTop: 8,
            fontFamily: 'Roboto',
            fontStyle: 'bold',
            fontWeight: 700,
            textDecoration: 'underline',
        },
        textSmIndented: {
            ...textSm,
            padding: '2px 0px 0px 10px',
        },
    });
    const { banking, pic, owner, payments, } = traderInfo;
    const { cardNumber, cardType, cardExpDate, cardCvc, keysource_secondary, } = payments;
    const { paymentMethod, } = keysource_secondary;
    const achIsSelected = paymentMethod === 'ACH';
    const ccIsSelected = paymentMethod === 'Credit Card';
    const cardholderInfo = [
        { text: `Business Name:  ${pharmacy.name}`, style: styles.field, },
        {
            text: `Billing Address:  ${pharmacy.address}, ${pharmacy.city}, ${pharmacy.state} ${pharmacy.zipCode}`, style: styles.field,
        },
        {
            text: `Person in Charge (PIC):  ${pic.firstName} ${pic.lastName}     Phone:  ${pic.phone}`,
            style: styles.field,
        },
        { text: `Email:  ${pic.email}`, style: styles.field, },
    ];
    const [cardInfo, achInfo, Signatures] = useMemo(() => {
        const date = dayjs().format('MM/DD/YYYY');
        const cardInfo = [
            {
                text: `Card Number: ${ccIsSelected ? ` ${cardNumber}` : '_____________________'}     Expiration: ${ccIsSelected ? ` ${cardExpDate}` : '_______________'}     Security Code: ${ccIsSelected ? ` ${cardCvc}` : '________'}`,
                style: styles.field,
            },
            {
                text: `Cardholder Signature:  ${ccIsSelected ? '                                     ' : '______________________________'}     Date:  ${date}`,
                style: styles.field,
            },
        ];
        const achInfo = [
            {
                text: `Bank Name:  ${banking.bankName}`,
                style: styles.field,
            },
            {
                text: `Account Number:  ${banking.accountNumber}     Routing Number:  ${banking.routingNumber}`,
                style: styles.field,
            },
        ];
        const Signatures = (_jsxs(View, { style: styles.section2, children: [_jsx(Text, { style: { ...styles.textSm2, paddingTop: 5, }, children: `Printed Name:  ${owner.firstName} ${owner.lastName}     Signature:                                                          Date:  ${date}` }), _jsx(Text, { style: styles.textSm2Bold, children: "KeySource Acquisition, LLC:" }), _jsx(Text, { style: styles.textSm2, children: "Ryan Sevek: ___________________________________    Date: ___________________________________" })] }));
        return [cardInfo, achInfo, Signatures];
    }, [traderInfo]);
    const [achTerms, achOptions, achOptionsLen] = useMemo(() => {
        const achTerms = [
            {
                text: 'The ACH Payment Agreement (Agreement) is made by and between KeySource Acquisition, LLC, (dba KeySource) a Delaware Corporation and (Customer).',
                style: styles.textSm,
            },
            {
                text: 'I.   Terms',
                style: styles.textSmIndented,
            },
            {
                text: 'By providing the information below, customer provides KeySource the authority to initiate Automatic Clearing House (ACH) transactions from the bank account listed below. KeySource agrees to withdraw funds based upon due date of invoices outstanding on said Customer\'s account.',
                style: styles.textSm,
            },
        ];
        const achOptions = [
            { checked: false, span: false, text: 'Automatically charge credit card on invoice due date (Net 30)', },
            { checked: false, span: false, text: 'Automatically charge credit card as orders ship', },
            {
                checked: false,
                span: true,
                startText: 'Automatically charge credit card once a month for',
                boldText: 'full balance',
                endText: '(ran on the 1st business day)',
            },
            { checked: achIsSelected, span: false, text: 'Automatically initiate ACH on invoice due date', },
        ];
        return [achTerms, achOptions, achOptions.length];
    }, []);
    return (_jsx(Page, { size: 'A4', wrap: false, children: _jsxs(View, { style: styles.container, children: [_jsx(BorderPolygon, { border: 'top' }), _jsxs(View, { style: styles.content, children: [_jsxs(View, { style: styles.header, children: [_jsx(Text, { style: styles.slogan, children: "Your Trusted Source for Generic Pharmaceuticals" }), _jsx(ImageHeader, { showContact: false, width: 150 })] }), _jsx(Text, { style: styles.title, children: "Recurring Payment Authorization Form" }), _jsxs(View, { style: styles.section, children: [_jsx(Text, { style: styles.subtitle, children: "Cardholder Information" }), cardholderInfo.map((info, index) => (_jsx(Text, { style: info.style, children: info.text }, index)))] }), _jsxs(View, { style: styles.section, children: [_jsx(Text, { style: styles.subtitle, children: "Credit Card Information" }), _jsx(CardType, { cardType: cardType }), cardInfo.map((info, index) => (_jsx(Text, { style: info.style, children: info.text }, index)))] }), _jsxs(View, { style: styles.section, children: [_jsx(Text, { style: styles.subtitle, children: "ACH Payment Agreement" }), achTerms.map((term, index) => (_jsx(Text, { style: term.style, children: term.text }, index)))] }), _jsxs(View, { style: styles.section, children: [_jsx(Text, { style: styles.subtitle, children: "Banking Information" }), achInfo.map((term, index) => (_jsx(Text, { style: term.style, children: term.text }, index)))] }), _jsxs(View, { style: styles.section2, children: [_jsx(Text, { style: styles.subtitle2, children: "Provide a copy of a voided check in addition to the above information for validation." }), _jsx(Text, { style: styles.textSmIndented, children: "I.   Confidentiality" }), _jsx(Text, { style: styles.textSm, children: "All information relating to this Agreement, and the respective business and financial affairs of (customer) and KeySource shall be kept in strict confidence by the other party. The foregoing obligation does not apply to any information that has become publicly available, that is rightfully obtained from third parties who are not bound by any confidentiality requirement, or disclosures, which are required to be made under any state or federal law." })] }), _jsxs(View, { style: styles.section2, children: [_jsx(Text, { style: styles.subtitleIndented, children: "Select One:" }), achOptions.map((option, index) => (_jsx(Checkbox, { isLast: index === achOptionsLen - 1, ...option }, index))), _jsx(Text, { style: styles.textSm, children: "You agree that no prior-notification will be provided. Without authorization to run payment automatically, customers will not be eligible for a credit limit increase or term extension." })] }), Signatures] }), _jsx(BorderPolygon, { border: 'bottom' })] }) }));
});
